import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from 'baseui'
import { lightTheme } from '@/customTheme'
import Icons from '@/scenes/Editor/components/Icons'
import giftImage from '@/assets/images/limited-offer/gift-50-off.png'
import { CONFETTI_BIT_STYLES, LIMITED_OFFER_DISCOUNT_PERCENT } from './limitedOfferConfig'

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'

// When the card's height is clamped, this band gives up the space so the copy
// and actions below never clip.
const ArtBand = styled('div', {
  position: 'relative',
  height: '300px',
  flexShrink: 1,
  minHeight: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 4, 96, 0.08)',
})

const Scene = styled('div', {
  position: 'relative',
  width: '320px',
  height: '266.5px',
})

// Floats while open. Wraps the pop so each element owns one keyframe.
const GiftFloat = styled('div', {
  position: 'relative',
  zIndex: 2,
  animationName: {
    '0%': { transform: 'translateY(0) rotate(0deg)' },
    '50%': { transform: 'translateY(-6px) rotate(-0.8deg)' },
    '100%': { transform: 'translateY(0) rotate(0deg)' },
  } as any,
  animationDuration: '3.2s',
  animationTimingFunction: 'ease-in-out',
  animationDelay: '1200ms',
  animationIterationCount: 'infinite',
  [REDUCED_MOTION]: { animationName: 'none' },
})

// Pops in after the card has landed.
const Gift = styled('img', {
  display: 'block',
  width: '320px',
  height: '266.5px',
  animationName: {
    '0%': { opacity: 0, transform: 'scale(0.72) translateY(26px) rotate(-6deg)' },
    '55%': { opacity: 1, transform: 'scale(1.05) translateY(-6px) rotate(1.5deg)' },
    '100%': { opacity: 1, transform: 'none' },
  } as any,
  animationDuration: '620ms',
  animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  animationDelay: '340ms',
  animationFillMode: 'both',
  [REDUCED_MOTION]: { animationName: 'none' },
})

// Above the gift: behind it, the opaque illustration hides most of the flight.
const Confetti = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: 3,
  pointerEvents: 'none',
  [REDUCED_MOTION]: { display: 'none' },
})

// One keyframe serves every particle; each writes its own custom properties.
const ConfettiBit = styled('span', {
  position: 'absolute',
  left: '50%',
  top: '52%',
  width: 'var(--w)',
  height: 'var(--h)',
  background: 'var(--c)',
  borderRadius: 'var(--r)',
  opacity: 0,
  animationName: {
    '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.4) rotate(0deg)' },
    '12%': { opacity: 1 },
    '62%': {
      opacity: 1,
      transform: 'translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(1) rotate(var(--rot))',
    },
    '100%': {
      opacity: 0,
      transform:
        'translate(calc(-50% + var(--dx) * 1.18), calc(-50% + var(--dy) + 46px)) scale(0.9) rotate(calc(var(--rot) * 1.4))',
    },
  } as any,
  animationDuration: '1100ms',
  animationTimingFunction: 'cubic-bezier(0.12, 0.7, 0.3, 1)',
  animationDelay: 'var(--delay)',
  animationFillMode: 'both',
})

const CloseButton = styled('button', {
  position: 'absolute',
  top: '16px',
  right: '16px',
  width: '32px',
  height: '32px',
  padding: 0,
  border: 'none',
  borderRadius: '360px',
  background: lightTheme.colors.white,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background 0.15s',
  ':hover': { background: lightTheme.colors.grayScale50 },
  // Same keyboard ring as DefaultButton.
  ':focus-visible': { outline: `2px solid ${lightTheme.colors.primary}`, outlineOffset: '2px' },
})

interface Props {
  onClose: () => void
}

/** Animations start on mount; the owner remounts this on every open to replay them. */
function LimitedOfferArtwork({ onClose }: Props) {
  const { t } = useTranslation()

  return (
    <ArtBand>
      <Scene>
        <Confetti aria-hidden="true">
          {CONFETTI_BIT_STYLES.map((style, index) => (
            <ConfettiBit key={index} style={style as CSSProperties} />
          ))}
        </Confetti>
        <GiftFloat>
          <Gift
            src={giftImage}
            alt={t('An open gift box with a %@% off coupon', { arg: LIMITED_OFFER_DISCOUNT_PERCENT })}
          />
        </GiftFloat>
      </Scene>
      <CloseButton type="button" aria-label={t('Close')} onClick={onClose}>
        <Icons.Close size={24} />
      </CloseButton>
    </ArtBand>
  )
}

export default LimitedOfferArtwork
